import {createColumnHelper, getCoreRowModel, useReactTable} from '@tanstack/react-table';
import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {IntlShape, useIntl} from 'react-intl';
import Table from 'src/autobahn/components/table';
import Button from 'src/autobahn/components/button/button';
import IconButton from 'src/autobahn/components/button/icon-button';
import TablePageControls from 'src/autobahn/components/table-page-controls';
import useAlerts from 'src/hooks/use-alerts';
import useTimezone from 'src/hooks/use-timezone';
import {formatDate} from 'src/helpers/date';
import {formatDuration} from 'src/helpers/duration';
import {
  REPORT_TYPE_LABEL,
  reportFileBaseName,
  openSignedDownload,
  reportJobDownloadErrorMessage,
  reportJobDownloadUrl,
  reportJobErrorMessage,
  reportJobFailureMessage,
  saveReportFile,
} from 'src/helpers/report';
import {downloadReportJobFile} from 'src/services/reportService';
import {useCancelReportJob} from './mutations';
import ReportJobDevicesModal from './devices-modal';
import {ThemeContext} from 'src/apiContexts';
import sharedStyles from 'src/autobahn/shared/styles.module.scss';
import styles from './report-jobs-list.module.scss';
import type {ReportJob} from './queries';

/**
 * Nome do relatório (convenção reportFileBaseName), o mesmo na coluna "Relatório" e no arquivo baixado.
 */
const reportJobBaseName = (intl: IntlShape, job: ReportJob) => {
  const titleId = (REPORT_TYPE_LABEL as Record<string, string>)[job.reportType] || job.reportType;
  let from;
  let to;
  try {
    ({from, to} = JSON.parse(job.parameters || '{}'));
  } catch {
    // parameters malformado nunca deveria acontecer -- se acontecer, cai no "hoje" que
    // reportFileBaseName já usa para relatório sem período.
  }
  return reportFileBaseName({intl, titleId, deviceCount: job.deviceCount, from, to});
};

const STATUS_LABEL: Record<ReportJob['status'], string> = {
  pending: 'reportJobStatusPending',
  running: 'reportJobStatusRunning',
  done: 'reportJobStatusDone',
  failed: 'reportJobStatusFailed',
  cancelled: 'reportJobStatusCancelled',
  expired: 'reportJobStatusExpired',
};

const STATUS_CLASS: Record<ReportJob['status'], string> = {
  pending: styles.statusPending,
  running: styles.statusRunning,
  done: styles.statusDone,
  failed: styles.statusFailed,
  cancelled: styles.statusFailed,
  expired: styles.statusExpired,
};

const PAGE_SIZE_OPTIONS = {
  'ten': 10,
  'thirty': 30,
  'fifty': 50,
  'hundred': 100,
} as const;

/**
 * Contagem regressiva ao vivo enquanto o pedido ainda está na fila. `createdAt + estimatedSeconds`
 * é o instante em que deve ficar pronto -- cada tick recalcula contra o relógio real (não decrementa
 * um contador próprio), então não desvia mesmo com a aba em segundo plano. Reancora sozinho quando
 * `job` muda: o pedido de tras encurta em cadeia quando o da frente termina
 * (`ReportJobService#recalculateDownstreamEstimates`), e o socket `reportJobs` já reconsulta a lista
 * inteira nesse evento (ver container.tsx) -- o novo `estimatedSeconds` chega sozinho, sem reload.
 */
const EstimateCountdown = ({job}: {job: ReportJob}) => {
  const intl = useIntl();
  const targetMs = useMemo(
      () => new Date(job.createdAt).getTime() + job.estimatedSeconds * 1000,
      [job.createdAt, job.estimatedSeconds]);
  const remainingSecondsAt = () => Math.max(0, Math.round((targetMs - Date.now()) / 1000));
  const [remainingSeconds, setRemainingSeconds] = useState(remainingSecondsAt);

  useEffect(() => {
    setRemainingSeconds(remainingSecondsAt());
    const interval = setInterval(() => setRemainingSeconds(remainingSecondsAt()), 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMs]);

  // Prazo estimado vencido e o pedido ainda na fila: mostrar "0 s" parado dá a falsa ideia de que a tela
  // travou. A estimativa não é prazo fixo, então o texto diz que a geração segue.
  if (remainingSeconds === 0) {
    return <span>{intl.formatMessage({id: 'reportJobEstimateFinishing'})}</span>;
  }
  return <span>{formatDuration(intl, remainingSeconds)}</span>;
};

interface ReportJobsListProps {
  reportJobs: ReportJob[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  loading: boolean;
  currentUserId: number;
  /**
   * Pedido do link da notificação, só depois de o servidor ter respondido onde ele está (o container
   * passa `null` até lá). A lista realça a linha e avisa se o pedido não veio na página servida.
   */
  linkedJobId: number | null;
}

const columnHelper = createColumnHelper<ReportJob>();

const ReportJobsList = ({
  reportJobs, total, page, pageSize, onPageChange, onPageSizeChange, loading, currentUserId, linkedJobId,
}: ReportJobsListProps) => {
  const intl = useIntl();
  const {timezone} = useTimezone();
  const {toast} = useAlerts();
  const {mutate: cancelReportJob} = useCancelReportJob();
  const theme = useContext(ThemeContext);

  // O link da notificação de relatório pronto realça a linha na abertura, pelo mesmo mecanismo de
  // clique-pra-selecionar que os relatórios de resultado já usam (radio, cor sólida do tema da
  // central). Uma 2ª notificação com a tela aberta troca o linkedJobId sem remontar a lista.
  const [selectedJobId, setSelectedJobId] = useState<number | null>(linkedJobId);
  useEffect(() => {
    if (linkedJobId !== null) {
      setSelectedJobId(linkedJobId);
    }
  }, [linkedJobId]);

  // A paginação feita pelo usuário desfaz a seleção, para Dispositivos não abrir um pedido que saiu da tela. A página
  // adotada pelo link da notificação chega pela prop `page` e não passa por aqui, então o realce do link continua.
  const goToPage = (index: number) => {
    setSelectedJobId(null);
    onPageChange(index);
  };

  // A lista que o servidor devolve é a da conta de quem abriu o link: um jobId de outra conta, apagado
  // ou expirado não vem nela, e o aviso evita a tela sem nenhuma linha selecionada. O container só
  // passa o jobId depois de o servidor responder, então um pedido em outra página não cai aqui. O
  // aviso é uma vez por adoção do link: o container devolve `null` a cada nova navegação até a
  // resposta chegar, o que libera o aviso; um clique repetido num pedido ausente avisa de novo, e a
  // reconsulta da lista com o mesmo link adotado não.
  const linkWarnedForJobId = useRef<number | null>(null);
  useEffect(() => {
    if (linkedJobId === null) {
      linkWarnedForJobId.current = null;
      return;
    }
    if (loading || linkWarnedForJobId.current === linkedJobId) {
      return;
    }
    linkWarnedForJobId.current = linkedJobId;
    if (!reportJobs.some((job) => job.id === linkedJobId)) {
      toast.error(intl.formatMessage({id: 'reportJobLinkNotFound'}));
    }
  }, [linkedJobId, loading, reportJobs, toast, intl]);

  const handleDownload = async (job: ReportJob) => {
    try {
      const response = await downloadReportJobFile(job.id);
      const signedUrl = await reportJobDownloadUrl(response);
      if (signedUrl) {
        openSignedDownload(signedUrl, `${reportJobBaseName(intl, job)}.${job.format}`);
        return;
      }
      saveReportFile(response, job.format, reportJobBaseName(intl, job));
    } catch (err) {
      toast.error(await reportJobDownloadErrorMessage(intl, err));
    }
  };

  const handleCancel = (job: ReportJob) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(intl.formatMessage({id: 'reportJobCancelConfirm'}))) {
      return;
    }
    cancelReportJob(job.id, {
      onError: (error) => toast.error(reportJobErrorMessage(intl, error)),
    });
  };

  const columns = useMemo(() => [
    // Primeira coluna: a tabela rola para o lado em telas estreitas e a ação da linha precisa
    // continuar visível sem rolagem.
    columnHelper.display({
      id: 'action',
      header: '',
      cell: ({row}) => {
        const job = row.original;
        const isOwner = job.userId === currentUserId;
        const isCancellable = isOwner && (job.status === 'pending' || job.status === 'running');

        if (job.status === 'done') {
          return (
            <div className={sharedStyles.actionButtons}>
              <IconButton
                label={intl.formatMessage({id: 'reportJobDownload'})}
                className={sharedStyles.actionButton}
                onClick={() => handleDownload(job)}
              >
                <i className='fas fa-download' />
              </IconButton>
            </div>
          );
        }
        if (isCancellable) {
          return (
            <div className={sharedStyles.actionButtons}>
              <IconButton
                label={intl.formatMessage({id: 'reportJobCancel'})}
                className={sharedStyles.actionButton}
                onClick={() => handleCancel(job)}
              >
                <i className='fas fa-ban' />
              </IconButton>
            </div>
          );
        }
        return null;
      },
      meta: {className: sharedStyles.verticalAlignBaseline},
    }),
    columnHelper.accessor((job) => `${reportJobBaseName(intl, job)} (${job.format})`, {
      id: 'report',
      header: intl.formatMessage({id: 'reportJobColumnReport'}),
      cell: (info) => info.getValue(),
      meta: {className: sharedStyles.verticalAlignBaseline},
    }),
    columnHelper.accessor(
        (job) => formatDate(job.createdAt, intl.formatMessage({id: 'dateTimeLongFormat'}), timezone), {
          id: 'requested',
          header: intl.formatMessage({id: 'reportJobColumnRequested'}),
          cell: (info) => info.getValue(),
          meta: {className: sharedStyles.verticalAlignBaseline},
        }),
    columnHelper.accessor((job) => job.requesterEmail || '—', {
      id: 'requester',
      header: intl.formatMessage({id: 'reportJobColumnRequester'}),
      cell: (info) => info.getValue(),
      meta: {className: sharedStyles.verticalAlignBaseline},
    }),
    columnHelper.accessor((job) => intl.formatMessage({id: STATUS_LABEL[job.status]}), {
      id: 'status',
      header: intl.formatMessage({id: 'reportJobColumnStatus'}),
      cell: ({row, getValue}) => (
        <span
          className={`${styles.statusBadge} ${STATUS_CLASS[row.original.status]}`}
          title={row.original.status === 'failed' ? reportJobFailureMessage(intl, row.original.failure) : undefined}
        >
          {getValue()}
        </span>
      ),
      meta: {className: sharedStyles.verticalAlignBaseline},
    }),
    // Continua visível depois de concluído de propósito -- é o número que dá pra comparar com
    // "Gerado em" - "Solicitado em" e ver se bateu, não só uma promessa que some; por isso só o
    // pedido ainda em fila (pending/running) vira contagem regressiva, terminal volta a ser o
    // valor fixo de sempre.
    columnHelper.accessor((job) => job.estimatedSeconds, {
      id: 'estimate',
      header: intl.formatMessage({id: 'reportJobColumnEstimate'}),
      cell: ({row, getValue}) => {
        const job = row.original;
        const estimatedSeconds = getValue();
        const inQueue = job.status === 'pending' || job.status === 'running';
        if (!estimatedSeconds) {
          // Estimativa 0 em pedido ainda na fila: o servidor ainda está calculando.
          return inQueue ? intl.formatMessage({id: 'reportJobEstimateCalculating'}) : '—';
        }
        if (inQueue) {
          return <EstimateCountdown job={job} />;
        }
        return formatDuration(intl, estimatedSeconds);
      },
      meta: {className: `${sharedStyles.verticalAlignBaseline} ${styles.estimateCell}`},
    }),
    columnHelper.accessor(
        (job) => job.finishedAt ? formatDate(job.finishedAt, intl.formatMessage({id: 'dateTimeLongFormat'}), timezone) : '—', {
          id: 'generated',
          header: intl.formatMessage({id: 'reportJobColumnGenerated'}),
          cell: (info) => info.getValue(),
          meta: {className: sharedStyles.verticalAlignBaseline},
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [intl, timezone, currentUserId]);

  const [devicesModalOpen, setDevicesModalOpen] = useState(false);

  // Paginação de verdade, vinda do servidor (MAX_PAGE_SIZE lá) -- reportJobs já é só a página
  // atual, não a lista inteira, então manualPagination:true e nada de getPaginationRowModel: a
  // tabela não deve fatiar de novo em cima do que o servidor já fatiou.
  const table = useReactTable({
    data: reportJobs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: Math.max(1, Math.ceil(total / pageSize)),
    state: {
      pagination: {pageIndex: page, pageSize},
    },
  });

  // Fila vazia de verdade (total do servidor). Página vazia com total > 0 (os pedidos da última
  // página expiraram ou foram removidos) segue para a tabela, que mantém a paginação para voltar.
  if (!loading && total === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>{intl.formatMessage({id: 'reportJobsEmpty'})}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.actionBar}>
        <div className={styles.actionBarEnd}>
          <Button
            variant='secondary'
            disabled={selectedJobId === null}
            onClick={() => setDevicesModalOpen(true)}
          >
            <>{intl.formatMessage({id: 'reportJobDevicesButton'})}</>
          </Button>
        </div>
      </div>

      <ReportJobDevicesModal
        jobId={selectedJobId}
        open={devicesModalOpen}
        onClose={() => setDevicesModalOpen(false)}
      />

      <Table
        table={table}
        theme={theme}
        emptyMessage={intl.formatMessage({id: 'sharedNoDataText'})}
        onRowClick={(row) => setSelectedJobId(row.original.id)}
        isRowSelected={(row) => row.original.id === selectedJobId}
        selectedRowStyle={{backgroundColor: theme, color: '#fff'}}
      />

      <TablePageControls
        pageCount={table.getPageCount()}
        pageIndex={page}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={(size) => {
          setSelectedJobId(null);
          onPageSizeChange(size);
        }}
        setPageIndex={goToPage}
        getCanPreviousPage={() => page > 0}
        getCanNextPage={() => page < table.getPageCount() - 1}
        previousPage={() => goToPage(page - 1)}
        nextPage={() => goToPage(page + 1)}
      />
    </div>
  );
};

export default ReportJobsList;
